import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { areaStore } from "../stores/locales/area_store"
import { allDevicesStore } from "../stores/devices/all_devices_store"
import { allDeviceTypesStore } from "../stores/device_types/all_device_types_store"
import { allDeploymentStatusesStore } from "../stores/deployment_statuses/all_deployment_statuses_store"
import { allRoomsStore } from "../stores/locales/all_rooms_store"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import { getCsrfToken } from "shared/form_helpers"

// Connects to data-controller="spreadsheet-placement"
export default class extends Controller {

    static stores = [
        allDevicesStore,             // Write: append created devices (declared first for setter availability)
        allDeviceTypesStore,         // Read: icon lookups
        allDeploymentStatusesStore,  // Read: color lookups
        allRoomsStore,               // Read: room lookups
        areaStore                    // Read: current area context
    ]

    static targets = [
        "csvInput",           // File input element
        "workqueueSection",   // Workqueue section wrapper (header + collapsible)
        "workqueue",          // Workqueue list container
        "counter",            // "X of Y remaining" display
        "floorplanDropzone",  // Droppable area
        "errorDisplay",       // Error message area
        "completionMessage",  // "All placed" banner
        "placedList",         // Placed devices section wrapper
        "placedListItems"     // Placed devices list container
    ]

    static values = {
        devices: Array,            // Parsed CSV device records
        placedCount: Number,       // Number of successfully placed devices
        currentDragIndex: Number,  // Index of currently dragged item
        isDragging: Boolean        // Drag lock flag
    }

    connect() {
        useStore(this)

        // Set up beforeunload listener to warn about unplaced devices
        this.boundBeforeUnloadHandler = this.handleBeforeUnload.bind(this)
        window.addEventListener("beforeunload", this.boundBeforeUnloadHandler)

        // Droppable is initialized lazily when the workqueue is first rendered,
        // since jQuery UI is loaded via a page-specific pack that may not be ready yet.
        this.droppableInitialized = false
    }

    disconnect() {
        // Remove beforeunload listener
        if (this.boundBeforeUnloadHandler) {
            window.removeEventListener("beforeunload", this.boundBeforeUnloadHandler)
            this.boundBeforeUnloadHandler = null
        }

        // Clean up jQuery UI draggable instances on workqueue items
        if (this.hasWorkqueueTarget) {
            const $draggables = $(this.workqueueTarget).find(".ui-draggable")
            if ($draggables.length > 0) {
                $draggables.draggable("destroy")
            }
        }

        // Clean up jQuery UI droppable instance on floorplan
        if (this.hasFloorplanDropzoneTarget) {
            const $droppable = $(this.floorplanDropzoneTarget)
            if ($droppable.data("ui-droppable")) {
                $droppable.droppable("destroy")
            }
        }
    }

    /**
     * Reactive callback for area store changes.
     * Resets workqueue on area change, toggles CSV input enabled/disabled.
     * @param {object} area - The new area store value
     */
    onAreaStoreUpdate(area) {
        // If area changed and there are devices in the workqueue, clear the workqueue
        if (this.devicesValue.length > 0) {
            this.devicesValue = []
            this.placedCountValue = 0
            this.clearWorkqueueDisplay()
            this.updateCounter()
            sendFailureNotification("Area changed. Please upload a new CSV file for this area.")
        }

        // Remove any placed icons from the previous area
        this.clearPlacedIcons()

        // Toggle CSV input enabled/disabled based on area and floorplan
        this.toggleCsvInput(area)
    }

    /**
     * Removes all placed device icons rendered by this controller from the floorplan.
     * Called when the area changes to avoid icons persisting across different floorplans.
     */
    clearPlacedIcons() {
        if (!this.hasFloorplanDropzoneTarget) return
        const placedIcons = this.floorplanDropzoneTarget.querySelectorAll('[data-is-dropped-icon="true"]')
        placedIcons.forEach(icon => {
            // Dispose Bootstrap tooltip before removing
            const tooltip = bootstrap.Tooltip.getInstance(icon)
            if (tooltip) {
                tooltip.dispose()
            }
            icon.remove()
        })
    }

    /**
     * Enables or disables the CSV input based on area selection and floorplan availability.
     * @param {object} area - The current area store value
     */
    toggleCsvInput(area) {
        if (!this.hasCsvInputTarget) return

        if (!area || !area.id) {
            // No area selected → disable CSV input
            this.csvInputTarget.disabled = true
            return
        }

        if (!area.floor_plan_url) {
            // Area has no floorplan → disable CSV input and show message
            this.csvInputTarget.disabled = true
            this.showError("Please upload a floorplan for this area before placing devices.")
            return
        }

        // Area has floorplan → enable CSV input
        this.csvInputTarget.disabled = false
        this.clearError()
    }

    /**
     * Handles CSV file selection — delegates parsing to csv-parser controller,
     * renders workqueue on success or displays errors on failure.
     * @param {Event} event - The change event from the file input
     */
    async handleFileSelected(event) {
        const file = event.target.files[0]
        if (!file) return

        // Clear any previous state
        this.clearWorkqueueDisplay()
        this.clearError()

        // Get the csv-parser controller from the same element
        const csvParser = this.application.getControllerForElementAndIdentifier(
            this.element, "csv-parser"
        )

        if (!csvParser) {
            sendFailureNotification("CSV parser not available")
            return
        }

        // Delegate parsing to the csv-parser controller
        const result = await csvParser.parseFile(file)

        if (result.success) {
            // Detect devices already on the floorplan by checking both:
            // 1. The allDevicesStore (devices loaded for this area)
            // 2. The actual floorplan icon tooltips (catches any the store may have missed)
            const existingNames = this.getExistingDeviceNames()

            // Mark devices that already exist as "placed"
            this.devicesValue = result.devices.map(d => ({
                ...d,
                placed: existingNames.has(d.name.toLowerCase())
            }))

            this.placedCountValue = this.devicesValue.filter(d => d.placed).length
            this.renderWorkqueue()
            this.renderPlacedList()
            this.updateCounter()
        } else {
            this.displayErrors(result.errors)
        }

        // Reset file input so the same file can be re-uploaded
        event.target.value = ''
    }

    /**
     * Renders the workqueue list from the current devices array.
     * Each item shows device name, device type name, and room name.
     */
    renderWorkqueue() {
        if (!this.hasWorkqueueTarget) return

        const unplacedDevices = this.devicesValue.filter(d => !d.placed)

        // Show/hide the workqueue section based on whether there are devices to place
        if (this.hasWorkqueueSectionTarget) {
            if (unplacedDevices.length > 0) {
                this.workqueueSectionTarget.classList.remove('d-none')
            } else {
                this.workqueueSectionTarget.classList.add('d-none')
            }
        }

        const html = unplacedDevices
            .map((device) => `
                <div class="list-group-item workqueue-item" data-index="${device.index}">
                    <div class="fw-bold">${this.escapeHtml(device.name)}</div>
                    <small class="text-muted">${this.escapeHtml(device.device_type_name)} • ${this.escapeHtml(device.room_name)}</small>
                </div>
            `).join('')

        this.workqueueTarget.innerHTML = html

        // Initialize droppable once (lazy — waits for jQuery UI to be loaded)
        if (!this.droppableInitialized) {
            this.initializeDroppable()
            this.droppableInitialized = true
        }

        this.initializeDraggables()
    }

    /**
     * Renders the "Placed Devices" list showing devices that have been placed on the floorplan.
     * Items are non-draggable and support hover-to-spotlight behavior.
     */
    renderPlacedList() {
        if (!this.hasPlacedListTarget || !this.hasPlacedListItemsTarget) return

        const placedDevices = this.devicesValue.filter(d => d.placed)

        if (placedDevices.length === 0) {
            this.placedListTarget.classList.add('d-none')
            return
        }

        this.placedListTarget.classList.remove('d-none')

        const html = placedDevices.map(device => `
            <div class="list-group-item list-group-item-action placed-device-item py-2"
                 data-device-name="${this.escapeHtml(device.name)}"
                 data-action="mouseenter->spreadsheet-placement#spotlightDevice mouseleave->spreadsheet-placement#unspotlightDevice">
                <div class="fw-bold text-success"><i class="bi bi-check-circle-fill me-1"></i>${this.escapeHtml(device.name)}</div>
                <small class="text-muted">${this.escapeHtml(device.device_type_name)} • ${this.escapeHtml(device.room_name)}</small>
            </div>
        `).join('')

        this.placedListItemsTarget.innerHTML = html
    }

    /**
     * Spotlights the floorplan icon matching the hovered placed device.
     * Hides all other icons and highlights the matching one.
     * @param {Event} event - The mouseenter event
     */
    spotlightDevice(event) {
        const deviceName = event.currentTarget.dataset.deviceName
        if (!this.hasFloorplanDropzoneTarget) return

        // Get all icons in the existing-devices-container
        const deviceList = this.floorplanDropzoneTarget.querySelector('.existing-devices-container')
        if (deviceList) {
            const allIcons = deviceList.querySelectorAll('[data-bs-toggle="tooltip"]')
            allIcons.forEach(icon => {
                const title = icon.getAttribute('title') || icon.getAttribute('data-bs-original-title') || ''
                const iconName = title.split('<br>')[0].trim()
                if (iconName.toLowerCase() !== deviceName.toLowerCase()) {
                    icon.style.opacity = '0'
                    icon.style.pointerEvents = 'none'
                } else {
                    icon.style.opacity = '1'
                    icon.style.transform = 'scale(1.3)'
                }
            })
        }

        // Also check placed icons dropped by this controller
        const placedIcons = this.floorplanDropzoneTarget.querySelectorAll('[data-is-dropped-icon="true"]')
        placedIcons.forEach(icon => {
            const title = icon.getAttribute('title') || icon.getAttribute('data-bs-original-title') || ''
            const iconName = title.split('<br>')[0].trim()
            if (iconName.toLowerCase() !== deviceName.toLowerCase()) {
                icon.style.opacity = '0'
                icon.style.pointerEvents = 'none'
            } else {
                icon.style.opacity = '1'
                icon.style.transform = 'scale(1.3)'
            }
        })
    }

    /**
     * Removes the spotlight effect, restoring all floorplan icons to normal appearance.
     * @param {Event} event - The mouseleave event
     */
    unspotlightDevice(event) {
        if (!this.hasFloorplanDropzoneTarget) return

        const deviceList = this.floorplanDropzoneTarget.querySelector('.existing-devices-container')
        if (deviceList) {
            const allIcons = deviceList.querySelectorAll('[data-bs-toggle="tooltip"]')
            allIcons.forEach(icon => {
                icon.style.opacity = '1'
                icon.style.transform = 'scale(1)'
                icon.style.pointerEvents = ''
            })
        }

        const placedIcons = this.floorplanDropzoneTarget.querySelectorAll('[data-is-dropped-icon="true"]')
        placedIcons.forEach(icon => {
            icon.style.opacity = '1'
            icon.style.transform = 'scale(1)'
            icon.style.pointerEvents = ''
        })
    }

    /**
     * Initializes jQuery UI draggable on each workqueue item.
     * Displays a 30×30px semi-transparent device type icon at cursor during drag.
     * Implements drag lock to prevent a second drag while one is in progress.
     * Reverts the item with animation when dropped outside a valid droppable (floorplan).
     */
    initializeDraggables() {
        if (!this.hasWorkqueueTarget || !this.hasFloorplanDropzoneTarget) return

        const self = this

        $(this.workqueueTarget).find('.workqueue-item').draggable({
            helper: function() {
                // Get the device data for this item
                const index = parseInt($(this).attr('data-index'), 10)
                const device = self.devicesValue.find(d => d.index === index)
                const iconClass = device ? device.device_type_icon : 'fas fa-microchip'

                // Create 30×30px semi-transparent icon helper
                return $('<div/>')
                    .css({
                        width: '30px',
                        height: '30px',
                        opacity: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#6c757d',
                        borderRadius: '50%',
                        cursor: 'grabbing'
                    })
                    .append($('<i/>').addClass(iconClass).css({ fontSize: '16px', color: 'white' }))
            },
            revert: 'invalid',        // Revert animation when dropped outside valid droppable
            revertDuration: 300,
            cursor: 'grabbing',
            cursorAt: { left: 15, top: 15 },  // Center cursor on 30×30 helper
            zIndex: 1000,
            start: function(event, ui) {
                // Drag lock — prevent second drag while one is in progress
                if (self.isDraggingValue) {
                    return false
                }
                self.isDraggingValue = true
                self.currentDragIndexValue = parseInt($(this).attr('data-index'), 10)
            },
            stop: function(event, ui) {
                self.isDraggingValue = false
            }
        })
    }

    /**
     * Initializes jQuery UI droppable on the floorplan container.
     * Accepts workqueue items and calculates drop coordinates as percentages.
     */
    initializeDroppable() {
        if (!this.hasFloorplanDropzoneTarget) return

        const self = this

        $(this.floorplanDropzoneTarget).droppable({
            accept: '.workqueue-item',
            tolerance: 'pointer',
            drop: function(event, ui) {
                self.handleDrop(event, ui)
            },
            over: function(event, ui) {
                // Visual feedback when entering the drop zone
                $(this).addClass('drop-zone-active')
            },
            out: function(event, ui) {
                $(this).removeClass('drop-zone-active')
            }
        })
    }

    /**
     * Handles a drop event on the floorplan.
     * Calculates x/y coordinates in pixels relative to the floorplan container,
     * consistent with the existing floor_plan_icon_manager_controller coordinate system.
     * @param {Event} event - The jQuery UI drop event
     * @param {Object} ui - The jQuery UI drop ui object
     */
    handleDrop(event, ui) {
        const $dropzone = $(this.floorplanDropzoneTarget)
        $dropzone.removeClass('drop-zone-active')

        // Get the device index from the dragged item
        const deviceIndex = this.currentDragIndexValue
        const device = this.devicesValue.find(d => d.index === deviceIndex)
        if (!device) return

        // Calculate coordinates relative to the existing-devices-container (deviceList)
        // This matches how floor_plan_icon_manager_controller calculates coordinates
        // in handleFloorPlanClick: event.clientX - deviceListRect.left
        const deviceList = this.floorplanDropzoneTarget.querySelector('.existing-devices-container')
        const deviceListRect = deviceList ? deviceList.getBoundingClientRect() : this.floorplanDropzoneTarget.getBoundingClientRect()

        // Get the drop position (center of the helper — helper is 30×30px)
        // ui.offset gives us the helper's page offset, convert to client coordinates
        const helperCenterX = ui.offset.left - window.pageXOffset + 15
        const helperCenterY = ui.offset.top - window.pageYOffset + 15

        const xPixels = Math.round(helperCenterX - deviceListRect.left)
        const yPixels = Math.round(helperCenterY - deviceListRect.top)

        // Remove the jQuery UI helper element
        ui.helper.remove()

        // Submit the device directly to the API (no modal form)
        this.submitDevicePlacement(device, xPixels, yPixels)
    }

    /**
     * Submits a device placement directly to the API without showing a modal form.
     * Shows a toast notification on success or failure.
     * @param {Object} device - The device record from the workqueue
     * @param {Number} xPixels - The x coordinate in pixels
     * @param {Number} yPixels - The y coordinate in pixels
     */
    async submitDevicePlacement(device, xPixels, yPixels) {
        this.currentDropCoordinates = { x: xPixels, y: yPixels }
        this.currentDropDevice = device

        const area = this.areaStoreValue
        const payload = {
            locale: {
                region: area.region,
                building: area.building,
                area: area.id,
                room: device.room_id,
                floorplan_x_coordinate: String(Math.round(xPixels)),
                floorplan_y_coordinate: String(Math.round(yPixels))
            },
            devices: [{
                name: device.name,
                type: {
                    device_type: device.device_type_id,
                    manufacturer: device.manufacturer_id,
                    model: device.model_id
                },
                deployment_status: device.deployment_status_id,
                dns_name: device.dns || undefined,
                ip_address: device.ip_address || undefined,
                mac_address: device.mac_address || undefined,
                serial_number: device.serial_number || undefined
            }]
        }

        try {
            const response = await fetch('/api/items', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify(payload)
            })

            if (response.ok) {
                const responseData = await response.json()
                this.handleFormSuccess(responseData)
            } else if (response.status === 401) {
                window.location.href = '/users/sign_in'
            } else {
                let errorMessage = 'Failed to save device'
                try {
                    const errorData = await response.json()
                    errorMessage = errorData.message || errorMessage
                } catch (e) {}
                sendFailureNotification(`${device.name}: ${errorMessage}`)
            }
        } catch (error) {
            sendFailureNotification(`${device.name}: Network error — ${error.message || 'please try again'}`)
        }
    }

    /**
     * Handles a successful device placement — removes device from workqueue,
     * updates the allDevicesStore, and shows completion message if all placed.
     * @param {Object} responseData - The API response data
     */
    handleFormSuccess(responseData) {
        const device = this.currentDropDevice

        // Mark device as placed in the devices array
        const updatedDevices = this.devicesValue.map(d =>
            d.index === device.index ? { ...d, placed: true } : d
        )
        this.devicesValue = updatedDevices
        this.placedCountValue = this.placedCountValue + 1

        // Re-render both lists
        this.renderWorkqueue()
        this.renderPlacedList()

        // Update the counter
        this.updateCounter()

        // Render the placed device icon at the drop coordinates with drag support
        this.renderPlacedIcon(device, responseData)

        // Append the new device to allDevicesStore so future CSV uploads detect it
        const createdItem = responseData?.data?.items?.[0] || responseData?.items?.[0]
        if (createdItem) {
            const transformed = this.transformCreatedDevice(createdItem)
            const currentDevices = this.allDevicesStoreValue || []
            this.setAllDevicesStoreValue([...currentDevices, transformed])
        }

        // Show completion message if all devices are placed
        if (this.placedCountValue === this.devicesValue.length) {
            this.showCompletionMessage()
        }

        sendSuccessNotification(`${device.name} saved successfully`)
    }

    /**
     * Renders a device icon directly on the floorplan at the drop coordinates.
     * Includes tooltip and drag-to-reposition support.
     * @param {Object} device - The device record with coordinates and type info
     * @param {Object} responseData - The API response containing the created item ID
     */
    renderPlacedIcon(device, responseData) {
        if (!this.hasFloorplanDropzoneTarget) return

        const coords = this.currentDropCoordinates
        if (!coords) return

        const deviceId = responseData?.data?.items?.[0]?.id || responseData?.items?.[0]?.id
        const iconClass = (device.device_type_icon || 'fas fa-microchip').replace(/\bfas\b/g, 'fa')
        const statusColor = device.deployment_status_color || '#6c757d'

        // Create the icon element matching the floor_plan_icon_manager structure
        const wrapper = document.createElement('div')
        wrapper.style.position = 'absolute'
        wrapper.style.top = `${Math.round(coords.y)}px`
        wrapper.style.left = `${Math.round(coords.x)}px`
        wrapper.style.width = '30px'
        wrapper.style.height = '30px'
        wrapper.style.display = 'block'
        wrapper.style.textAlign = 'center'
        wrapper.style.lineHeight = '30px'
        wrapper.style.cursor = 'grab'
        wrapper.dataset.isDroppedIcon = 'true'
        wrapper.dataset.deviceId = deviceId || ''
        wrapper.className = 'existing_device'

        // Add tooltip
        wrapper.setAttribute('data-bs-toggle', 'tooltip')
        wrapper.setAttribute('data-bs-placement', 'top')
        wrapper.setAttribute('data-bs-html', 'true')
        wrapper.setAttribute('title', `${this.escapeHtml(device.name)}<br>${this.escapeHtml(device.deployment_status_name)}<br>${this.escapeHtml(device.room_name)}`)

        const badge = document.createElement('span')
        badge.className = 'badge badge-pill badge-primary'
        badge.style.backgroundColor = statusColor

        const icon = document.createElement('i')
        icon.className = iconClass
        icon.style.fontSize = '16px'
        icon.style.color = 'white'

        badge.appendChild(icon)
        wrapper.appendChild(badge)

        // Append to the existing-devices-container (same container the icon manager uses)
        const deviceList = this.floorplanDropzoneTarget.querySelector('.existing-devices-container')
        if (deviceList) {
            deviceList.appendChild(wrapper)
        } else {
            this.floorplanDropzoneTarget.appendChild(wrapper)
        }

        // Initialize Bootstrap tooltip
        new bootstrap.Tooltip(wrapper)

        // Add drag support for repositioning
        this.addDragSupport(wrapper, deviceId)
    }

    /**
     * Adds mouse-based drag support to a placed icon for repositioning.
     * On drag end, updates the device's coordinates via the API.
     * @param {HTMLElement} iconElement - The icon wrapper element
     * @param {string} deviceId - The device's UUID for API updates
     */
    addDragSupport(iconElement, deviceId) {
        let isDragging = false
        let startX, startY, origLeft, origTop

        const onMouseDown = (e) => {
            e.preventDefault()
            isDragging = true
            startX = e.clientX
            startY = e.clientY
            origLeft = parseInt(iconElement.style.left, 10)
            origTop = parseInt(iconElement.style.top, 10)
            iconElement.style.cursor = 'grabbing'
            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
        }

        const onMouseMove = (e) => {
            if (!isDragging) return
            const dx = e.clientX - startX
            const dy = e.clientY - startY
            iconElement.style.left = `${origLeft + dx}px`
            iconElement.style.top = `${origTop + dy}px`
        }

        const onMouseUp = (e) => {
            if (!isDragging) return
            isDragging = false
            iconElement.style.cursor = 'grab'
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)

            // Persist the new position via API
            const newX = parseInt(iconElement.style.left, 10)
            const newY = parseInt(iconElement.style.top, 10)

            if (deviceId && (newX !== origLeft || newY !== origTop)) {
                fetch('/api/items', {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': getCsrfToken()
                    },
                    body: JSON.stringify({
                        id: deviceId,
                        locale: {
                            floorplan_x_coordinate: newX,
                            floorplan_y_coordinate: newY
                        }
                    })
                }).catch(err => console.error('[spreadsheet-placement] Failed to update position:', err))
            }
        }

        iconElement.addEventListener('mousedown', onMouseDown)
    }

    /**
     * Transforms a created device from the API response into the format expected by allDevicesStore.
     * @param {Object} item - Raw item from API response (flat format from format_item_for_response)
     * @returns {Object} Transformed device for the store
     */
    transformCreatedDevice(item) {
        const device = this.currentDropDevice
        return {
            id: item.id,
            name: item.name,
            ip_address: item.ip_address,
            mac_address: item.mac_address,
            dns_name: item.dns_name,
            locale: {
                region: { id: item.region },
                building: { id: item.building },
                area: { id: item.area },
                room: { id: item.room, name: device?.room_name || null },
                floorplan_x_coordinate: item.floorplan_x_coordinate || String(Math.round(this.currentDropCoordinates?.x || 0)),
                floorplan_y_coordinate: item.floorplan_y_coordinate || String(Math.round(this.currentDropCoordinates?.y || 0))
            },
            deployment_status: {
                id: item.deployment_status_id || item.deployment_status,
                name: device?.deployment_status_name || null
            },
            device: {
                device_type: {
                    id: item.device_type,
                    name: device?.device_type_name || null,
                    icon: device?.device_type_icon || null
                },
                manufacturer: { id: item.manufacturer, name: null },
                model: { id: item.model, name: null }
            }
        }
    }

    /**
     * Shows the completion message when all devices have been placed.
     */
    showCompletionMessage() {
        if (!this.hasCompletionMessageTarget) return
        this.completionMessageTarget.classList.remove('d-none')
        this.completionMessageTarget.textContent = 'All devices have been successfully placed!'
    }

    /**
     * Displays validation errors in the error display area (scrollable, max 100 errors shown).
     * @param {Array} errors - Array of error objects with message property
     */
    displayErrors(errors) {
        if (!this.hasErrorDisplayTarget) return

        const errorCount = errors.length
        const errorItems = errors.map(e => `<li>${this.escapeHtml(e.message)}</li>`).join('')

        this.errorDisplayTarget.classList.remove("d-none")
        this.errorDisplayTarget.innerHTML = `
            <div class="alert alert-danger">
                <h6>Validation Errors (${errorCount} error${errorCount !== 1 ? 's' : ''} found)</h6>
                <div class="overflow-auto" style="max-height: 300px;">
                    <ul class="list-unstyled mb-0">${errorItems}</ul>
                </div>
            </div>
        `
    }

    /**
     * Escapes HTML special characters to prevent XSS when rendering user-provided content.
     * @param {string} text - The text to escape
     * @returns {string} HTML-safe string
     */
    escapeHtml(text) {
        const div = document.createElement('div')
        div.appendChild(document.createTextNode(text))
        return div.innerHTML
    }

    /**
     * beforeunload handler — warns if there are unplaced devices
     * @param {BeforeUnloadEvent} event
     */
    handleBeforeUnload(event) {
        if (this.devicesValue.length > 0 && this.placedCountValue < this.devicesValue.length) {
            event.preventDefault()
            event.returnValue = ""
        }
    }

    /**
     * Clears the workqueue DOM display
     */
    clearWorkqueueDisplay() {
        if (this.hasWorkqueueTarget) {
            this.workqueueTarget.innerHTML = ""
        }
        if (this.hasWorkqueueSectionTarget) {
            this.workqueueSectionTarget.classList.add("d-none")
        }
        if (this.hasCompletionMessageTarget) {
            this.completionMessageTarget.classList.add("d-none")
            this.completionMessageTarget.textContent = ""
        }
        if (this.hasPlacedListTarget) {
            this.placedListTarget.classList.add("d-none")
        }
    }

    /**
     * Updates the counter display with remaining device count
     */
    updateCounter() {
        if (!this.hasCounterTarget) return

        const total = this.devicesValue.length
        const remaining = total - this.placedCountValue

        if (total === 0) {
            this.counterTarget.textContent = ""
        } else {
            this.counterTarget.textContent = `${remaining} of ${total} devices remaining`
        }
    }

    /**
     * Displays an error message in the error display area
     * @param {string} message - The error message to display
     */
    showError(message) {
        if (!this.hasErrorDisplayTarget) return
        this.errorDisplayTarget.classList.remove("d-none")
        this.errorDisplayTarget.innerHTML = `<div class="alert alert-warning">${message}</div>`
    }

    /**
     * Clears the error display area
     */
    clearError() {
        if (!this.hasErrorDisplayTarget) return
        this.errorDisplayTarget.classList.add("d-none")
        this.errorDisplayTarget.innerHTML = ""
    }

    /**
     * Collects existing device names from both the allDevicesStore and the floorplan icons.
     * Uses tooltip text (first line before <br>) as the device name for icon detection.
     * @returns {Set<string>} Lowercased device names currently on the floorplan
     */
    getExistingDeviceNames() {
        const names = new Set()

        // Source 1: allDevicesStore (devices loaded for this area)
        const storeDevices = this.allDevicesStoreValue || []
        storeDevices.forEach(d => {
            if (d.name) names.add(d.name.toLowerCase())
        })

        // Source 2: Floorplan icons (tooltip text — first line is the device name)
        if (this.hasFloorplanDropzoneTarget) {
            const deviceList = this.floorplanDropzoneTarget.querySelector('.existing-devices-container')
            if (deviceList) {
                const icons = deviceList.querySelectorAll('[data-bs-toggle="tooltip"]')
                icons.forEach(icon => {
                    const title = icon.getAttribute('title') || icon.getAttribute('data-bs-original-title') || ''
                    // Tooltip format: "Name<br>Status<br>Room" — extract the first line
                    const name = title.split('<br>')[0].trim()
                    if (name) names.add(name.toLowerCase())
                })
            }
        }

        return names
    }

    /**
     * Downloads a CSV template file with the required and optional headers.
     * Generates the file client-side and triggers a download.
     * @param {Event} event - The click event
     */
    downloadTemplate(event) {
        event.preventDefault()

        const headers = ['name', 'device_type', 'manufacturer', 'model', 'deployment_status', 'room', 'dns', 'ip_address', 'mac_address', 'serial_number']
        const exampleRow = ['Example Device', 'Workstation', 'Dell', 'OptiPlex 7090', 'Active', 'Room 101', 'device.local', '192.168.1.100', 'AA:BB:CC:DD:EE:FF', 'SN-12345']

        const csvContent = headers.join(',') + '\n' + exampleRow.join(',') + '\n'

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'device_placement_template.csv'
        link.click()
        URL.revokeObjectURL(url)
    }
}
